// The Win Probability % and Stage cells' shared popover body (spec 008 §2.4): the stage picker, a
// divider, Close as Won / Lost, then Update / Cancel. Both cells open THIS one editor.
//
// D6: no query. The bands come from the page (ctx.bands), which already loads the account's mapping —
// the modal this replaces ran its own ForecastQueryRenderer on every open. Custom stages from Stage
// Mapping therefore appear with no extra work.
// D10: reopenToOpportunity is computed from status !== OPPORTUNITY exactly as the modal did. A won
// opportunity is a converted project whose win chance the backend reports as 100% regardless of what is
// stored, so without this a changed value silently does not persist.
// D11: Close as Won / Lost commit immediately through the existing setWinProbability helper — same
// mutation, same from -> to banner, same copy as today's kebab items.
import React, {useState} from 'react';
import {Alert, Button, EMPHASIS, Label} from '@forecast-it/design-system';
import Styled from 'styled-components';
import {WinProbabilityStageBands} from '../../../shared/components/opportunities/WinProbabilityStageBands';
import {changeWinChance} from '../../../project-tab/projects/project-settings/ProjectSettingsMutations';
import {createToast} from '../../../shared/components/toasts/toast';
import {setWinProbability} from '../../opportunityRowActions';
import {bandForWinChance, toPercent, type StageBand} from '../../opportunityStage';
import {PROJECT_STATUS} from '../../../../constants';
import type {OpportunityNode} from '../../opportunityTypes';

export type EditorFormatMessage = (descriptor: {id: string}, values?: Record<string, string>) => string;

export interface WinProbabilityStageEditorProps {
	node: OpportunityNode;
	bands: StageBand[];
	formatMessage: EditorFormatMessage;
	onCommit: () => void;
	close: () => void;
	title: string;
	closeAsLabel: string;
	wonLabel: string;
	lostLabel: string;
	updateLabel: string;
	cancelLabel: string;
}

const Body = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 700px;
	max-width: 90vw;
	padding: 16px;
`;
const Divider = Styled.span`
	height: 1px;
	background: #e4e4e7;
`;
const CloseAs = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;
const Actions = Styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 8px;
`;

export const WinProbabilityStageEditor = ({
	node,
	bands,
	formatMessage,
	onCommit,
	close,
	title,
	closeAsLabel,
	wonLabel,
	lostLabel,
	updateLabel,
	cancelLabel,
}: WinProbabilityStageEditorProps): JSX.Element => {
	// Unset defaults to 100%, matching the DB default and the modal this replaces.
	const stored = node.baselineWinChance ?? 1;
	const [winChance, setWinChance] = useState(stored);

	// "<win probability %> (<stage>)", e.g. "40% (Qualified)" — the existing from -> to banner phrasing.
	const bandStage = (wp: number): string => {
		const resolved = bandForWinChance(bands, wp);
		if (resolved.kind === 'band') return `${toPercent(wp)}% (${resolved.band.name})`;
		if (resolved.kind === 'lost') return `0% (${formatMessage({id: 'opportunities.stage.lost'})})`;
		if (resolved.kind === 'won') return `100% (${formatMessage({id: 'opportunities.stage.won'})})`;
		return '';
	};

	const save = () => {
		// Reachable only for a changed value (Update is disabled otherwise), so re-opening a converted
		// opportunity here is always for a genuinely new value.
		changeWinChance(node.id, winChance, node.status !== PROJECT_STATUS.OPPORTUNITY);
		const message = formatMessage(
			{id: 'opportunities.update_win_probability.toast'},
			{project: node.name ?? '', from: bandStage(stored), to: bandStage(winChance)}
		);
		createToast({duration: 5000, variant: 'banner', message: <Alert type={Alert.TYPE.SUCCESS} text={message} />});
		onCommit();
		close();
	};

	const closeAs = (value: 1 | 0) => {
		close();
		setWinProbability(node, value, onCommit, formatMessage, bands);
	};

	return (
		<Body data-cy="win-probability-editor">
			<Label>{title}</Label>
			{/* 0% and 100% are deliberately absent from the picker — they are reached only through Close as
			    Lost / Won, so choosing a stage can never convert an opportunity as a side effect. */}
			<WinProbabilityStageBands bands={bands} value={winChance} onChange={setWinChance} />
			<Divider />
			<CloseAs>
				<Label>{closeAsLabel}</Label>
				{/* Disabled at the value they would set, the same guard the kebab's Set-to-100% / Set-to-0%
				    items carried: closing an already-won opportunity as Won changes nothing but still
				    commits a mutation and refetches the whole list. Compared against the stored chance,
				    not the picker draft - the draft is what Update commits, these two are separate. */}
				<Button emphasis={EMPHASIS.MEDIUM} data-cy="close-as-won" disabled={stored === 1} onPress={() => closeAs(1)}>
					{wonLabel}
				</Button>
				<Button emphasis={EMPHASIS.MEDIUM} data-cy="close-as-lost" disabled={stored === 0} onPress={() => closeAs(0)}>
					{lostLabel}
				</Button>
			</CloseAs>
			<Actions>
				<Button emphasis={EMPHASIS.LOW} data-cy="win-probability-cancel" onPress={close}>
					{cancelLabel}
				</Button>
				<Button
					emphasis={EMPHASIS.HIGH}
					data-cy="win-probability-submit"
					disabled={winChance === stored}
					onPress={save}
				>
					{updateLabel}
				</Button>
			</Actions>
		</Body>
	);
};

export default WinProbabilityStageEditor;
